import { useState, useRef, useEffect } from "react";
import { Table, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

function TopEstudiantesTable({ estudiantes, title = "Top Estudiantes con Más Atenciones" }) {
  const [cantidadMostrada, setCantidadMostrada] = useState(5);
  const [cargandoMas, setCargandoMas] = useState(false);
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();

  // Mapeo de carreras
  const carrerasMap = {
    "PC": "Plan Común",
    "DCC": "Ing. Informática",
    "DFI": "Ing. Física",
    "DAS": "Ing. Astronomía",
    "DGF": "Ing. Geofísica",
    "DG": "Ing. Geología",
    "DIC": "Ing. Civil",
    "IQBT": "Ing. Química",
    "DIE": "Ing. Eléctrica",
    "DII": "Ing. Industrial",
    "DIM": "Ing. Matemática",
    "DIMEC": "Ing. Mecánica",
    "DIMIN": "Ing. de Minas",
  };

  // Mapeo de tipo de estudiante a colores
  const tipoEstudianteColors = {
    "pregrado": "primary",
    "postgrado": "secondary",
    "egresado": "success",
    "externo": "warning",
  };

  // Resetear cantidad mostrada cuando cambien los estudiantes
  useEffect(() => {
    setCantidadMostrada(5);
  }, [estudiantes]);

  // Manejar scroll infinito
  const handleScroll = (e) => {
    const element = e.target;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;

    // Si llegamos al 90% del scroll y hay más estudiantes para mostrar
    if (scrollPosition >= scrollHeight * 0.9 && cantidadMostrada < (estudiantes?.length || 0) && !cargandoMas) {
      setCargandoMas(true);
      
      // Simular pequeño delay para mejor UX
      setTimeout(() => {
        setCantidadMostrada(prev => Math.min(prev + 5, estudiantes?.length || 0));
        setCargandoMas(false);
      }, 300);
    }
  };

  const estudiantesAMostrar = estudiantes?.slice(0, cantidadMostrada) || [];

  // Manejar click en fila para ir a ficha del estudiante
  const handleRowClick = (idEstudiante) => {
    navigate(`/dashboard/ficha/${idEstudiante}`);
  };

  // Estilos para la barra de scroll personalizada
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 12px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    
    .table-scroll-container {
      display: block;
      width: 100%;
      overflow-x: auto;
      overflow-y: scroll;
    }
    
    .table-scroll-container table {
      width: 100%;
      margin-bottom: 0;
    }
    
    .table-scroll-container thead {
      position: sticky;
      top: 0;
      z-index: 10;
      background: #f8f9fa;
    }
    
    .clickable-row {
      cursor: pointer;
      transition: background-color 0.2s ease;
    }
    
    .clickable-row:hover {
      background-color: rgba(13, 110, 253, 0.1) !important;
    }
  `;

  return (
    <div className="card shadow-sm">
      <style>{scrollbarStyles}</style>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">
            <i className="bi bi-graph-up-arrow me-2"></i>
            {title}
          </h5>
          {estudiantes && estudiantes.length > cantidadMostrada && (
            <small className="text-muted">
              Mostrando {cantidadMostrada} de {estudiantes.length} • Desplázate para ver más
            </small>
          )}
        </div>

        {estudiantes && estudiantes.length > 0 ? (
          <div 
            ref={tableContainerRef}
            onScroll={handleScroll}
            className="table-scroll-container custom-scrollbar" 
            style={{ 
              maxHeight: '280px',
              height: '280px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#888 #f1f1f1'
            }}
          >
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-center" style={{ width: "80px" }}>Posición</th>
                  <th>Nombre</th>
                  <th>RUT</th>
                  <th>Carrera</th>
                  <th className="text-center">Total Atenciones</th>
                  <th className="text-center">Tipo</th>
                  <th>Último Funcionario</th>
                </tr>
              </thead>
              <tbody>
                {estudiantesAMostrar.map((estudiante, index) => (
                  <tr 
                    key={estudiante.id || index}
                    onClick={() => handleRowClick(estudiante.id)}
                    className="clickable-row"
                  >
                    <td className="text-center">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "36px",
                          height: "36px",
                          backgroundColor: index === 0 ? "#e3f2fd" : index === 1 ? "#f3e5f5" : index === 2 ? "#e0f2f1" : "#f5f5f5",
                          color: index === 0 ? "#1976d2" : index === 1 ? "#7b1fa2" : index === 2 ? "#00796b" : "#757575",
                          fontWeight: "600",
                        }}
                      >
                        {index + 1}
                      </div>
                    </td>
                    <td className="fw-semibold">{estudiante.nombre}</td>
                    <td className="text-muted">{estudiante.rut}</td>
                    <td>{carrerasMap[estudiante.carrera] || estudiante.carrera}</td>
                    <td className="text-center">
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: "#e3f2fd",
                          color: "#1976d2",
                          fontSize: "0.9rem",
                          padding: "6px 12px",
                        }}
                      >
                        {estudiante.total_atenciones}
                      </span>
                    </td>
                    <td className="text-center">
                      <Badge bg={tipoEstudianteColors[estudiante.tipo_estudiante] || "secondary"}>
                        {estudiante.tipo_estudiante.charAt(0).toUpperCase() + estudiante.tipo_estudiante.slice(1)}
                      </Badge>
                    </td>
                    <td className="text-muted">{estudiante.ultimo_funcionario}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Espaciador para permitir scroll cuando hay pocos elementos */}
            {estudiantes && estudiantes.length > cantidadMostrada && (
              <div style={{ height: '100px' }}></div>
            )}
            
            {/* Indicador de carga al final de la tabla */}
            {cargandoMas && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" variant="primary" />
                <small className="text-muted ms-2">Cargando más estudiantes...</small>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox" style={{ fontSize: "3rem" }}></i>
            <p className="mt-3">No hay datos disponibles</p>
          </div>
        )}
      </div>
    </div>
  );
}

TopEstudiantesTable.propTypes = {
  estudiantes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
      rut: PropTypes.string,
      carrera: PropTypes.string,
      tipo_estudiante: PropTypes.string,
      total_atenciones: PropTypes.number,
      ultimo_funcionario: PropTypes.string,
    })
  ),
  title: PropTypes.string,
};

export default TopEstudiantesTable;
